import React, { FunctionComponent, ReactElement, useEffect } from "react";

import { ILocalizedExplicitValidationError } from "@multiweb/reactControls";
import { useEffectWithInitialDependencies } from "@multiweb/reactUtils";
import { LocalizationMultiLoader } from "../LocalizationLoader";
import { ValidationErrorTooltip } from "../ValidationErrorTooltip";

export type ValidationErrorWrapperComponentProps = {
    /** Unikátní identifikátor tooltipu. */
    tooltipId?: string;
    /**
     * Validační chyby. Zobrazí se v tooltipu při najetí myši. (Pokud není zadána `requiredInvalidError`.)
     *
     * Při jejich změně dojde k provolání `onValidityChanged`.
     */
    validationErrors: ReadonlyArray<ILocalizedExplicitValidationError>;
    /**
     * Hláška, že pole je povinné, ale není vyplněno (platnou hodnotou).
     * - Pokud zadána, pak se v tooltipu - místo `errors` - zobrazí pouze tato hláška.
     * - Pozn. Toto není validační chyba! Proto musí být evidováno samostatně. A nesmí to ovlivnit volání `onValidityChanged`.
     */
    requiredInvalidError?: ILocalizedExplicitValidationError;
    /** Závislosti pro `onRegister`. Při změně závislostí je nutné `onRegister` provolat znovu. */
    registerDependencies: ReadonlyArray<any>;
    /** Lokalizační klíče, k nimž bude načtena lokalizace. */
    locKeysToLoad: ReadonlyArray<string>;
    /** Callback pro zaregistrování validací do reduxu. */
    onRegister: () => void;
    /** Callback volaný při změně `validationErrors`. */
    onValidityChanged: () => void;
    /** Validované control. */
    children: ReactElement;
};

/**
 * Pomocná komponenta pro `ValidationErrorWrapper`. Zajišťuje:
 * - Provolání zadané registrace, když je potřeba.
 * - Provolání zadané `onValidityChanged` při změně validačních chyb.
 * - Načtení lokalizací potřebných pro `ValidationErrorWrapper`. (Zejména pro validační chyby zobrazované v tooltipu.)
 * - Deleguje zobrazení tooltipu na {@link ValidationErrorTooltip}.
 */
export const ValidationErrorWrapperComponent: FunctionComponent<ValidationErrorWrapperComponentProps> = ({
    tooltipId,
    validationErrors,
    requiredInvalidError,
    registerDependencies,
    locKeysToLoad,
    onRegister,
    onValidityChanged,
    children,
}) => {
    useEffect(() => {
        onRegister();
        // Záměrně se nevrací odregistrační funkce. Tento wrapper není zodpovědný za vytváření/mazání dataKey instance v Reduxu.
        // Pouze za registraci validací do existující instance.
        // (A není důvod při unmountu/updatu validace odregistrovávat.)
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, registerDependencies);

    // Pozn. onValidityChanged se vyvolá při každé změně instance validationErrors.
    //  - I když obsahově se validationErrors nezměnila.
    //  - Je však důležité volat validationErrors jen, když se nezměnila obsahově.
    //  - Proto je nutné zvenku zajistit, aby se instance validationErrors změnila jen, pokud se změnil její obsah.
    //  - Např. použitím selektoru createStableGetMergedValidationErrors z modulu `controls`, který toto zajišťuje.
    useEffectWithInitialDependencies(onValidityChanged, [validationErrors], [validationErrors]);

    const tooltipErrors = requiredInvalidError ? [requiredInvalidError] : validationErrors;
    return (
        <LocalizationMultiLoader locKeys={locKeysToLoad}>
            <ValidationErrorTooltip id={tooltipId} errors={tooltipErrors}>
                {children}
            </ValidationErrorTooltip>
        </LocalizationMultiLoader>
    );
};
